import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, FileText, Check, X, MousePointer2, PenTool, Image as ImageIcon, Trash2 } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  container: {
    padding: '20px 0',
    color: 'var(--text)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginBottom: '24px',
  },
  stats: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    height: '36px',
    paddingRight: '16px',
    boxShadow: '0 4px 10px rgba(0,0,0,0.04)',
  },
  statsIcon: {
    background: 'var(--accent)',
    width: '44px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    marginRight: '12px',
    clipPath: 'polygon(0% 0%, 85% 0%, 100% 50%, 85% 100%, 0% 100%)',
  },
  statsText: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: 'var(--text)',
    fontWeight: 700,
  },
  editorArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    alignItems: 'center',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    background: 'var(--surface)',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    marginBottom: '16px',
    position: 'sticky',
    top: '20px',
    zIndex: 100,
    backdropFilter: 'blur(16px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  toolBtn: (active) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    borderRadius: '8px',
    border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
    background: active ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
    color: active ? 'var(--accent)' : 'var(--text)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: active ? '0 0 15px rgba(59, 130, 246, 0.2)' : 'none',
  }),
  pagesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    width: '100%',
    maxWidth: '850px',
  },
  pageWrapper: {
    position: 'relative',
    background: '#fff',
    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
    borderRadius: '4px',
    overflow: 'visible',
    cursor: 'crosshair',
  },
  pageImg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    userSelect: 'none',
    pointerEvents: 'none',
  },
  signatureOverlay: (isSelected) => ({
    position: 'absolute',
    border: isSelected ? '2px solid var(--accent)' : '1px dashed var(--accent)',
    background: isSelected ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
    cursor: 'move',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    touchAction: 'none',
    zIndex: isSelected ? 10 : 1,
  }),
  signatureImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    pointerEvents: 'none',
  },
  handle: {
    position: 'absolute',
    width: '10px',
    height: '10px',
    background: 'var(--accent)',
    border: '2px solid #fff',
    borderRadius: '50%',
    zIndex: 20,
  },
  rotateHandle: {
    position: 'absolute',
    top: '-30px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '24px',
    height: '24px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  sigActions: {
    position: 'absolute',
    bottom: '-32px',
    right: '0',
    display: 'flex',
    gap: '4px',
  },
  sigActionBtn: {
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text)',
    transition: 'all 0.2s',
    '&:hover': {
        background: 'var(--surface2)',
    }
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(15, 23, 42, 0.4)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '60px',
    zIndex: 1000,
    backdropFilter: 'blur(12px)',
  },
  modalContent: {
    background: 'var(--surface)',
    borderRadius: '20px',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    padding: '16px 24px',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: 'var(--text)',
    flex: 1,
  },
  tabs: {
    display: 'flex',
    background: '#f8fafc',
    margin: '16px 24px',
    padding: '4px',
    borderRadius: '12px',
    gap: '4px',
    border: '1px solid #edf2f7',
  },
  tab: (active) => ({
    flex: 1,
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    background: active ? '#fff' : 'transparent',
    color: active ? 'var(--accent)' : '#718096',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  }),
  tabContent: {
    padding: '0 24px 24px',
    flex: 1,
    overflowY: 'auto',
  },
  sectionLabel: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#4a5568',
    marginBottom: '8px',
    marginTop: '20px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: 'var(--text)',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.2s',
    '&:focus': {
        borderColor: 'var(--accent)',
    }
  },
  fontGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    marginTop: '8px',
  },
  fontItem: (font, active, inkColor) => ({
    padding: '24px',
    borderRadius: '12px',
    border: active ? '2px solid var(--accent)' : '1px solid #e2e8f0',
    background: active ? 'rgba(22, 163, 74, 0.04)' : '#fff',
    fontFamily: font,
    fontSize: '28px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: inkColor,
    position: 'relative',
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  checkIcon: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    background: 'var(--accent)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  colorPicker: {
    display: 'flex',
    gap: '12px',
    marginTop: '12px',
    alignItems: 'center',
  },
  colorCircle: (color, active) => ({
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: color,
    cursor: 'pointer',
    border: active ? '2px solid var(--accent)' : '2px solid transparent',
    boxShadow: 'inset 0 0 0 2px #fff',
    transition: 'transform 0.1s',
    '&:hover': {
        transform: 'scale(1.1)',
    }
  }),
  canvasContainer: (inkColor) => {
    // Generate SVG with dynamic color
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='${inkColor === '#000000' ? 'black' : inkColor}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z'/><path d='m15 5 4 4'/></svg>`;
    const base64 = btoa(svg);
    return {
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        cursor: `url("data:image/svg+xml;base64,${base64}") 0 24, crosshair`,
        width: '100%',
        height: '300px',
        position: 'relative',
        overflow: 'hidden',
    };
  },
  canvas: {
    width: '100%',
    height: '100%',
  },
  stampGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },
  stampItem: {
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid var(--border)',
    background: 'var(--surface2)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    '&:hover': {
        borderColor: 'var(--accent)',
    }
  },
  modalFooter: {
    padding: '20px 24px',
    background: '#f8fafc',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    borderTop: '1px solid #edf2f7',
  },
  btn: (primary) => ({
    padding: '10px 24px',
    borderRadius: '10px',
    border: primary ? 'none' : '1px solid #e2e8f0',
    background: primary ? 'var(--accent)' : '#fff',
    color: primary ? '#fff' : '#4a5568',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s',
    '&:hover': {
        background: primary ? 'var(--accent-dark)' : '#f7fafc',
    }
  })
};

const SignatureModal = ({ onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('draw');
  const [color, setColor] = useState('#000000');
  const [thickness, setThickness] = useState(3);
  const [typedText, setTypedText] = useState('');
  const [selectedFont, setSelectedFont] = useState('Pacifico');
  const [error, setError] = useState('');
  
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  const fonts = ['Pacifico', 'Dancing Script', 'Great Vibes', 'Satisfy', 'Caveat'];
  const colors = ['#000000', '#0000ff', '#ff0000'];

  useEffect(() => {
    if (activeTab === 'draw' && canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }
  }, [activeTab]);

  const startDrawing = (e) => {
    setIsDrawing(true);
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => onSave(ev.target.result);
        reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (activeTab === 'draw') {
        onSave(canvasRef.current.toDataURL('image/png'));
    } else if (activeTab === 'type') {
        if (!typedText.trim()) {
            setError('Please type your name first');
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = 600;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.font = `60px ${selectedFont}`;
        ctx.fillStyle = color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedText || 'Signature', 300, 100);
        onSave(canvas.toDataURL('image/png'));
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.modalHeader}>
            <div style={{ background: 'rgba(22, 163, 74, 0.1)', padding: '8px', borderRadius: '8px', color: 'var(--accent)' }}>
                <PenTool size={20} />
            </div>
            <h3 style={styles.modalTitle}>Add Signature</h3>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }} onClick={onCancel}>
                <X size={24} />
            </button>
        </div>

        <div style={styles.tabs}>
            <button style={styles.tab(activeTab === 'draw')} onClick={() => setActiveTab('draw')}>
                <PenTool size={18} /> Draw
            </button>
            <button style={styles.tab(activeTab === 'type')} onClick={() => setActiveTab('type')}>
                <FileText size={18} /> Type
            </button>
            <button style={styles.tab(activeTab === 'upload')} onClick={() => setActiveTab('upload')}>
                <RotateCw size={18} /> Upload
            </button>
            <button style={styles.tab(activeTab === 'stamps')} onClick={() => setActiveTab('stamps')}>
                <FileText size={18} /> Stamps
            </button>
        </div>

        <div style={styles.tabContent}>
            {activeTab === 'draw' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #edf2f7' }}>
                        <div style={{ ...styles.colorPicker, marginTop: 0 }}>
                            {colors.map(c => (
                                <div key={c} style={styles.colorCircle(c, color === c)} onClick={() => setColor(c)} />
                            ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '12px', color: '#718096', fontWeight: 600 }}>Thickness</span>
                            <input type="range" min="1" max="10" value={thickness} onChange={(e) => setThickness(parseInt(e.target.value))} />
                        </div>
                        <button style={{ ...styles.btn(false), padding: '6px 12px', fontSize: '12px' }} onClick={clear}>Clear</button>
                    </div>
                    <div style={styles.canvasContainer(color)}>
                        <canvas 
                            ref={canvasRef}
                            style={styles.canvas}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={() => setIsDrawing(false)}
                            onMouseOut={() => setIsDrawing(false)}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={() => setIsDrawing(false)}
                        />
                    </div>
                </>
            )}

            {activeTab === 'type' && (
                <>
                    <div style={styles.sectionLabel}>Type your name</div>
                    <input 
                        type="text" 
                        placeholder="Type your name..." 
                        style={{ ...styles.input, borderColor: error ? '#ef4444' : '#e2e8f0' }}
                        value={typedText}
                        onChange={(e) => {
                            setTypedText(e.target.value);
                            setError('');
                        }}
                    />
                    {error && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', fontWeight: 600 }}>{error}</div>}
                    
                    <div style={styles.sectionLabel}>Choose a signature style</div>
                    <div style={styles.fontGrid}>
                        {fonts.map(f => (
                            <div key={f} style={styles.fontItem(f, selectedFont === f, color)} onClick={() => setSelectedFont(f)}>
                                {typedText || 'Signature'}
                                {selectedFont === f && (
                                    <div style={styles.checkIcon}>
                                        <Check size={12} strokeWidth={3} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={styles.sectionLabel}>Ink color</div>
                    <div style={styles.colorPicker}>
                        {colors.map(c => (
                            <div key={c} style={styles.colorCircle(c, color === c)} onClick={() => setColor(c)} />
                        ))}
                    </div>
                </>
            )}

            {activeTab === 'upload' && (
                <div style={{ height: '300px', border: '2px dashed #e2e8f0', borderRadius: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', background: '#f8fafc', marginTop: '20px' }}>
                    <div style={{ background: '#fff', padding: '16px', borderRadius: '50%', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                        <ImageIcon size={40} color="var(--accent)" />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ fontWeight: 600, color: '#4a5568' }}>Upload an image</p>
                        <p style={{ fontSize: '13px', color: '#718096', marginTop: '4px' }}>Supports PNG, JPG (transparent recommended)</p>
                    </div>
                    <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} id="sig-upload" />
                    <button style={styles.btn(true)} onClick={() => document.getElementById('sig-upload').click()}>Choose File</button>
                </div>
            )}

            {activeTab === 'stamps' && (
                <div style={{ ...styles.stampGrid, marginTop: '20px' }}>
                    {['APPROVED', 'REJECTED', 'DRAFT', 'SIGN HERE', 'CONFIDENTIAL'].map(text => (
                        <div key={text} style={styles.stampItem} onClick={() => {
                            const canvas = document.createElement('canvas');
                            canvas.width = 200;
                            canvas.height = 80;
                            const ctx = canvas.getContext('2d');
                            ctx.strokeStyle = text === 'REJECTED' ? '#ef4444' : '#3b82f6';
                            ctx.lineWidth = 4;
                            ctx.strokeRect(5, 5, 190, 70);
                            ctx.font = 'bold 24px Arial';
                            ctx.fillStyle = text === 'REJECTED' ? '#ef4444' : '#3b82f6';
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(text, 100, 40);
                            onSave(canvas.toDataURL('image/png'));
                        }}>
                            <div style={{ padding: '8px', border: '2px solid #e2e8f0', borderRadius: '4px', color: text === 'REJECTED' ? '#ef4444' : '#3b82f6', fontWeight: 'bold', fontSize: '12px' }}>{text}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>

        <div style={styles.modalFooter}>
            <button style={styles.btn(false)} onClick={onCancel}>Cancel</button>
            <button style={styles.btn(true)} onClick={handleSave}>
                <Check size={18} /> Add to PDF
            </button>
        </div>
      </div>
    </div>
  );
};

const DraggableSignature = ({ sig, dims, onUpdate, onRemove, isSelected, onSelect }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [activeHandle, setActiveHandle] = useState(null);

    const handleMouseDown = (e, type, handle = null) => {
        e.stopPropagation();
        onSelect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        setStartPos({ x: clientX, y: clientY });
        if (type === 'drag') setIsDragging(true);
        if (type === 'resize') {
            setIsResizing(true);
            setActiveHandle(handle);
        }
        if (type === 'rotate') setIsRotating(true);
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging && !isResizing && !isRotating) return;
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            const dx = clientX - startPos.x;
            const dy = clientY - startPos.y;

            if (isDragging) {
                onUpdate({ ...sig, x: sig.x + dx, y: sig.y + dy });
            } else if (isResizing) {
                let { x, y, w, h } = sig;
                const ratio = sig.w / sig.h;
                const useShift = e.shiftKey;

                if (activeHandle === 'br') {
                    w = Math.max(20, w + dx);
                    if (useShift) { h = w / ratio; } else { h = Math.max(10, h + dy); }
                } else if (activeHandle === 'bl') {
                    const newW = Math.max(20, w - dx);
                    if (newW > 20) { 
                        w = newW;
                        if (useShift) { h = w / ratio; x = sig.x + (sig.w - w); } else { x += dx; h = Math.max(10, h + dy); }
                    }
                } else if (activeHandle === 'tr') {
                    w = Math.max(20, w + dx);
                    const newH = Math.max(10, h - dy);
                    if (newH > 10) {
                        h = newH;
                        if (useShift) { w = h * ratio; y = sig.y + (sig.h - h); } else { y += dy; }
                    }
                } else if (activeHandle === 'tl') {
                    const newW = Math.max(20, w - dx);
                    const newH = Math.max(10, h - dy);
                    if (useShift) {
                        if (Math.abs(dx) > Math.abs(dy)) { w = newW; h = w / ratio; } else { h = newH; w = h * ratio; }
                        x = sig.x + (sig.w - w); y = sig.y + (sig.h - h);
                    } else {
                        if (newW > 20) { x += dx; w = newW; }
                        if (newH > 10) { y += dy; h = newH; }
                    }
                }
                onUpdate({ ...sig, x, y, w, h });
            } else if (isRotating) {
                const rect = document.getElementById(`sig-${sig.id}`).getBoundingClientRect();
                const center = {
                    x: rect.left + rect.width / 2,
                    y: rect.top + rect.height / 2
                };
                const angle = Math.atan2(clientY - center.y, clientX - center.x) * (180 / Math.PI);
                onUpdate({ ...sig, rotation: angle + 90 });
            }
            setStartPos({ x: clientX, y: clientY });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
            setIsRotating(false);
            setActiveHandle(null);
        };
        // ... rest of useEffect (event listeners)

        if (isDragging || isResizing || isRotating) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleMouseMove);
            window.addEventListener('touchend', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDragging, isResizing, isRotating, sig, startPos, onUpdate]);

    return (
        <div 
            id={`sig-${sig.id}`}
            style={{
                ...styles.signatureOverlay(isSelected),
                left: `${(sig.x / dims.w) * 100}%`,
                top: `${(sig.y / dims.h) * 100}%`,
                width: `${(sig.w / dims.w) * 100}%`,
                height: `${(sig.h / dims.h) * 100}%`,
                transform: `rotate(${sig.rotation || 0}deg)`,
            }}
            onMouseDown={(e) => handleMouseDown(e, 'drag')}
        >
            <img src={sig.image} style={styles.signatureImg} alt="signature" />
            
            {isSelected && (
                <>
                    {/* Corner Handles */}
                    <div style={{ ...styles.handle, left: '-5px', top: '-5px', cursor: 'nwse-resize' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'tl')} />
                    <div style={{ ...styles.handle, right: '-5px', top: '-5px', cursor: 'nesw-resize' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'tr')} />
                    <div style={{ ...styles.handle, left: '-5px', bottom: '-5px', cursor: 'nesw-resize' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'bl')} />
                    <div style={{ ...styles.handle, right: '-5px', bottom: '-5px', cursor: 'nwse-resize' }} onMouseDown={(e) => handleMouseDown(e, 'resize', 'br')} />

                    <div 
                        style={styles.rotateHandle} 
                        onMouseDown={(e) => handleMouseDown(e, 'rotate')}
                    >
                        <RotateCw size={14} />
                    </div>
                    <div style={styles.sigActions}>
                        <div style={styles.sigActionBtn} onClick={(e) => { e.stopPropagation(); onRemove(sig.id); }}>
                            <Trash2 size={16} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

const PagePreview = ({ file, pageIndex, onPlaceSignature, signatures, onUpdateSignature, onRemoveSignature, selectedId, onSelectSignature, mode, activeSignature, activeSignatureDims }) => {
  const [thumb, setThumb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [mousePos, setMousePos] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('page', pageIndex);
        
        const inspectRes = await fetch(`${API_BASE}/convert/inspect`, { method: 'POST', body: formData });
        if (inspectRes.ok) {
            const data = await inspectRes.json();
            if (active) setDims({ w: data.dimensions[0], h: data.dimensions[1] });
        }

        const renderRes = await fetch(`${API_BASE}/render-pdf`, { method: 'POST', body: formData });
        if (renderRes.ok && active) {
          const blob = await renderRes.blob();
          setThumb(URL.createObjectURL(blob));
        }
      } catch (e) {} finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; if (thumb) URL.revokeObjectURL(thumb); };
  }, [file, pageIndex]);

  const handleMouseMove = (e) => {
    if (mode !== 'place' || !activeSignature) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
    });
  };

  const handleMouseDownPlacement = (e) => {
    if (mode !== 'place' || !activeSignature) return;
    
    // Allow clicking the wrapper or the image
    const isWrapper = e.target === wrapperRef.current;
    const isBg = e.target.dataset.pagebg === 'true';
    if (!isWrapper && !isBg) return;

    e.stopPropagation();
    
    const rect = wrapperRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    
    const pdfX = (x / rect.width) * dims.w;
    const pdfY = (y / rect.height) * dims.h;
    
    onPlaceSignature(pageIndex, pdfX, pdfY);
    setMousePos(null);
  };

  return (
    <div 
        ref={wrapperRef} 
        style={{ ...styles.pageWrapper, cursor: mode === 'place' ? 'none' : 'crosshair' }} 
        onMouseDown={handleMouseDownPlacement}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setMousePos(null)}
    >
      {loading ? (
        <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RotateCw className="animate-spin" size={32} color="var(--text-muted)" />
        </div>
      ) : (
        <>
            <img src={thumb} alt={`page ${pageIndex+1}`} style={styles.pageImg} data-pagebg="true" />
            
            {/* Ghost Preview */}
            {mode === 'place' && activeSignature && mousePos && (
                <div style={{
                    position: 'absolute',
                    left: mousePos.x,
                    top: mousePos.y,
                    width: activeSignatureDims ? `${activeSignatureDims.w}px` : '150px',
                    height: activeSignatureDims ? `${activeSignatureDims.h}px` : '50px',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    opacity: 0.6,
                    zIndex: 100,
                }}>
                    <img src={activeSignature} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="ghost" />
                </div>
            )}

            {signatures.filter(s => s.page === pageIndex).map((sig) => (
                <DraggableSignature 
                    key={sig.id}
                    sig={sig}
                    dims={dims}
                    isSelected={selectedId === sig.id}
                    onSelect={() => onSelectSignature(sig.id)}
                    onUpdate={(updated) => onUpdateSignature(updated)}
                    onRemove={onRemoveSignature}
                />
            ))}
        </>
      )}
      <div style={{ position: 'absolute', bottom: '8px', left: '8px', fontSize: '12px', background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '2px 8px', borderRadius: '4px', pointerEvents: 'none' }}>
          Page {pageIndex + 1}
      </div>
    </div>
  );
};

export default function PDFSign({ file, onFinish }) {
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signatures, setSignatures] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeSignature, setActiveSignature] = useState(null);
  const [activeSignatureDims, setActiveSignatureDims] = useState({ w: 150, h: 50 });
  const [mode, setMode] = useState('select');
  const [selectedId, setSelectedId] = useState(null);
  const [savedSignatures, setSavedSignatures] = useState([]);

  useEffect(() => {
    const loadInfo = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API_BASE}/convert/pages`, { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          setPageCount(data.totalPages);
        }
      } catch (e) {} finally { setLoading(false); }
    };
    loadInfo();
  }, [file]);

  const handleSaveSignature = (dataUrl, shouldSave = true, dims = { w: 150, h: 50 }) => {
    setActiveSignature(dataUrl);
    setActiveSignatureDims(dims);
    if (shouldSave) {
        setSavedSignatures([...savedSignatures, dataUrl]);
    }
    setShowModal(false);
    setMode('place');
  };

  const handlePlaceSignature = (page, x, y, width = 150, height = 50) => {
    if (mode !== 'place' || !activeSignature) {
        setSelectedId(null);
        return;
    }
    
    const newSig = {
      id: Math.random().toString(36).substr(2, 9),
      page,
      x: x - width / 2,
      y: y - height / 2,
      w: width,
      h: height,
      rotation: 0,
      image: activeSignature
    };
    
    setSignatures([...signatures, newSig]);
    setSelectedId(newSig.id);
    setMode('select');
    setActiveSignature(null);
  };

  const handleUpdateSignature = (updated) => {
    setSignatures(signatures.map(s => s.id === updated.id ? updated : s));
  };

  const handleRemoveSignature = (id) => {
    setSignatures(signatures.filter(s => s.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  useEffect(() => {
    onFinish(signatures);
  }, [signatures, onFinish]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '40px' }}>
        <RotateCw className="animate-spin" size={32} color="var(--accent)" />
        <p style={{ marginTop: '12px', color: 'var(--text-muted)' }}>Analyzing PDF...</p>
    </div>
  );

  const handlePlaceShape = (type) => {
    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 24;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (type === 'check') {
        ctx.beginPath();
        ctx.moveTo(80, 200);
        ctx.lineTo(180, 300);
        ctx.lineTo(340, 100);
        ctx.stroke();
    } else if (type === 'cross') {
        ctx.beginPath();
        ctx.moveTo(100, 100);
        ctx.lineTo(300, 300);
        ctx.moveTo(300, 100);
        ctx.lineTo(100, 300);
        ctx.stroke();
    } else if (type === 'circle') {
        ctx.beginPath();
        ctx.arc(200, 200, 140, 0, Math.PI * 2);
        ctx.stroke();
    }

    handleSaveSignature(canvas.toDataURL('image/png'), false, { w: 24, h: 24 });
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.stats}>
          <div style={styles.statsIcon}><PenTool size={20} /></div>
          <div style={styles.statsText}>Sign PDF</div>
        </div>
      </div>

      <div style={styles.editorArea}>
        <div style={styles.toolbar}>
          <button 
            style={styles.toolBtn(mode === 'select')} 
            onClick={() => { setMode('select'); setActiveSignature(null); }}
          >
            <MousePointer2 size={18} /> Select
          </button>
          <button 
            style={styles.toolBtn(false)} 
            onClick={() => setShowModal(true)}
          >
            <PenTool size={18} /> Add Signature
          </button>

          <div style={{ width: '1px', background: 'var(--border)', margin: '0 8px' }} />

          <button 
            style={styles.toolBtn(false)} 
            onClick={() => handlePlaceShape('check')}
            title="Hold down the Shift key on your keyboard while resizing to get a better output."
          >
            <Check size={18} /> Check
          </button>
          <button 
            style={styles.toolBtn(false)} 
            onClick={() => handlePlaceShape('cross')}
            title="Hold down the Shift key on your keyboard while resizing to get a better output."
          >
            <X size={18} /> Cross
          </button>
          <button 
            style={styles.toolBtn(false)} 
            onClick={() => handlePlaceShape('circle')}
            title="Hold down the Shift key on your keyboard while resizing to get a better output."
          >
            <div style={{ width: '18px', height: '18px', border: '2px solid currentColor', borderRadius: '50%' }} /> Circle
          </button>
          
          {activeSignature && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '12px', borderLeft: '1px solid var(--border)', marginLeft: '12px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Click on page to place:</span>
                <img src={activeSignature} style={{ height: '32px', border: '1px solid var(--accent)', background: '#fff', borderRadius: '4px' }} alt="pending" />
                <button style={{ ...styles.sigActionBtn, background: 'transparent', border: 'none' }} onClick={() => { setActiveSignature(null); setMode('select'); }}>
                    <X size={16} />
                </button>
            </div>
          )}
        </div>

        {savedSignatures.length > 0 && (
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center', 
            background: 'var(--surface)', 
            padding: '12px 20px', 
            borderRadius: '12px', 
            border: '1px solid var(--border)',
            width: '100%',
            maxWidth: '850px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <span 
              style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', cursor: 'help', whiteSpace: 'nowrap' }}
              title="Your signatures are saved temporarily for this session"
            >
              Sign Saved:
            </span>
            <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
              {savedSignatures.map((sig, idx) => (
                <div 
                  key={idx} 
                  style={{ 
                    minWidth: '80px', 
                    height: '50px', 
                    border: activeSignature === sig ? '2px solid var(--accent)' : '1px solid var(--border)',
                    borderRadius: '8px',
                    background: '#fff',
                    cursor: 'pointer',
                    padding: '4px',
                    transition: 'all 0.2s',
                    boxShadow: activeSignature === sig ? '0 4px 12px rgba(59, 130, 246, 0.25)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onClick={() => {
                    setActiveSignature(sig);
                    setMode('place');
                  }}
                >
                  <img src={sig} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="saved sig" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={styles.pagesList}>
          {Array.from({ length: pageCount }).map((_, i) => (
            <PagePreview 
              key={i}
              file={file}
              pageIndex={i}
              signatures={signatures}
              selectedId={selectedId}
              onSelectSignature={setSelectedId}
              onPlaceSignature={(p, x, y) => handlePlaceSignature(p, x, y, activeSignatureDims.w, activeSignatureDims.h)}
              onUpdateSignature={handleUpdateSignature}
              onRemoveSignature={handleRemoveSignature}
              mode={mode}
              activeSignature={activeSignature}
              activeSignatureDims={activeSignatureDims}
            />
          ))}
        </div>
      </div>

      {showModal && (
        <SignatureModal 
          onSave={handleSaveSignature}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
